import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Button,
  Typography,
  CircularProgress
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { tokens } from '../../theme';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';

import MarketTabs from './MarketTabs';
import LeaderboardDialog from './LeaderboardDialog';
import InfoDialog from './InfoDialog';
import splinterlandsService from '../../services/SplinterlandsService';

/**
 * Shared market header component for all market views
 * Provides consistent header with tabs, info and leaderboard buttons
 * 
 * @param {Object} props - Component props
 * @param {string} [props.title="Splinterlands Market"] - Title to display in the header
 * @param {Function} [props.onInfoClick] - Callback when info button is clicked (if provided, internal state is not used)
 * @param {Function} [props.onLeaderboardClick] - Callback when leaderboard button is clicked (if provided, internal state is not used)
 * @param {React.ReactNode} [props.additionalControls] - Optional additional controls to display below tabs (e.g., sort dropdown)
 */
const MarketHeader = ({ 
  title = "Splinterlands Market",
  onInfoClick,
  onLeaderboardClick,
  additionalControls
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  
  // Dialog states (used only when not using external callbacks)
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [showInfo, setShowInfo] = useState(false);

  // Bonus Pool DEC balance
  const [bonusPoolBalance, setBonusPoolBalance] = useState(0);
  const [poolLoading, setPoolLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const loadBonusPool = async () => {
      try {
        const balance = await splinterlandsService.getBonusPoolBalance();
        if (isMounted) setBonusPoolBalance(balance);
      } catch (err) {
        console.error('Error loading bonus pool:', err);
      } finally {
        if (isMounted) setPoolLoading(false);
      }
    };

    loadBonusPool();

    return () => {
      isMounted = false;
    };
  }, []);

  const formatDec = (value) => {
    return Number(value || 0).toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  };

  // Check if using external dialog state management
  const usingExternalInfoState = !!onInfoClick;
  const usingExternalLeaderboardState = !!onLeaderboardClick;
  
  // Handle info button click
  const handleInfoClick = () => {
    if (usingExternalInfoState) {
      onInfoClick();
    } else {
      setShowInfo(true);
    }
  };
  
  // Handle leaderboard button click
  const handleLeaderboardClick = () => {
    if (usingExternalLeaderboardState) {
      onLeaderboardClick();
    } else {
      setShowLeaderboard(true);
    }
  };

  return (
    <>
      {/* Ultra-Compact Single Row Header */}
      <Paper 
        elevation={2}
        sx={{
          mb: 3,
          backgroundColor: colors.primary[400],
          borderRadius: 2,
          border: `1px solid ${colors.primary[300]}`,
          overflow: 'hidden',
          // Make header sticky only on desktop, not on mobile
          position: { xs: 'relative', md: 'sticky' },
          top: { xs: 'auto', md: '80px' },
          zIndex: 1000
        }}
      >
        {/* Single Row - Tabs + Bonus Pool + Leaderboard + Sort */}
        <Box 
          display="flex" 
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={2}
          sx={{ px: 2, py: 1 }}
        >
          {/* Left - Tabs */}
          <Box display="flex" alignItems="center" gap={1.5} sx={{ flex: '1 1 auto', minWidth: 0 }}>
            <MarketTabs integrated compact />
          </Box>

          {/* Right - Bonus Pool DEC + Leaderboard + Sort */}
          <Box display="flex" alignItems="center" gap={1.5} sx={{ flex: '0 0 auto' }}>
            {/* Bonus Pool DEC - Compact Display */}
            <Box
              sx={{
                display: { xs: 'none', md: 'flex' },
                alignItems: 'center',
                gap: 0.75,
                px: 1.25,
                py: 0.5,
                backgroundColor: colors.primary[500],
                borderRadius: 1,
                border: `1px solid ${colors.primary[300]}`
              }}
            >
              {poolLoading ? (
                <CircularProgress size={12} sx={{ color: colors.greenAccent[500] }} />
              ) : (
                <Typography variant="caption" fontWeight="600" sx={{ color: colors.greenAccent[400] }}>
                  {formatDec(bonusPoolBalance)} DEC
                </Typography>
              )}
              <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                Bonus Pool
              </Typography>
            </Box>

            {/* Leaderboard Button - Compact */}
            <Button
              variant="contained"
              startIcon={<EmojiEventsIcon sx={{ fontSize: 14 }} />}
              onClick={handleLeaderboardClick}
              sx={{
                backgroundColor: colors.greenAccent[600],
                color: colors.grey[100],
                fontWeight: '600',
                px: 1.25,
                py: 0.4,
                fontSize: '0.75rem',
                textTransform: 'none',
                minHeight: 'auto',
                '&:hover': { backgroundColor: colors.greenAccent[700] }
              }}
            >
              Leaderboard
            </Button>
            
            {/* Sort Controls */}
            {additionalControls && (
              <>
                <Box sx={{ display: { xs: 'none', lg: 'block' }, height: 20, width: '1px', backgroundColor: colors.primary[300] }} />
                {additionalControls}
              </>
            )}
          </Box>
        </Box>
      </Paper>

      {/* Dialogs */}
      {!usingExternalInfoState && (
        <InfoDialog open={showInfo} onClose={() => setShowInfo(false)} />
      )}
      {!usingExternalLeaderboardState && (
        <LeaderboardDialog open={showLeaderboard} onClose={() => setShowLeaderboard(false)} />
      )}
    </>
  );
};

export default MarketHeader;
